/**
 * Module 65 (WP-65.A2) — generating a board for a track position.
 *
 * The counterpart to `FreeLevelGenerator`, and it exists for one reason that is
 * not obvious until you look at what happens after a track HOLDS.
 *
 * ## Why `FreeLevelGenerator` cannot be reused as-is
 *
 * Both of Block Jam's anti-repetition mechanisms are keyed on the LEVEL NUMBER,
 * and that was correct for a numbered game where the level number changed with
 * every board:
 *
 *  - `FreeLevelGenerator.seen` is a `Map<level, signature>`, and the avoid-window
 *    is levels `n - 5 … n - 1`;
 *  - `RecipeChain.historyBefore(n)` walks forward to `n - 1` and returns the
 *    recipes of the levels before it.
 *
 * Once a track reaches its ceiling the effective level stops moving. Ask
 * `FreeLevelGenerator` for level 25 two hundred times and it answers the same
 * avoid-window every time (levels 20–24) — so consecutive endless puzzles are
 * never compared against **each other**, which is precisely the comparison that
 * matters when the level number can no longer distinguish them.
 *
 * So the layout window is re-keyed onto the ORDINAL here: a ring of the last
 * `antiRepetition.noRepeatWindow` signatures this track actually produced,
 * regardless of which effective level produced them.
 *
 * ## What is deliberately NOT fixed here
 *
 * `RecipeChain` is still frozen during the hold — the shapes-and-colours draw is
 * judged against the recipes of the levels below the ceiling, not against the
 * previous endless puzzle. That is left alone on purpose until it is measured:
 * the recipe RNG is fed by `variant`, so consecutive draws differ anyway, and
 * the question is whether they differ ENOUGH. WP-65.A2's report answers it with
 * a consecutive-repeat count rather than a guess. See the module PLAN.
 *
 * Sequential by construction, like `FreeLevelGenerator`: a board depends on the
 * boards before it, so walking ordinals in order is the only way to reproduce
 * what a player sees.
 */
import { RecipeChain } from './level-recipe';
import { generateFreeLevel } from './free-generator';
import { DEFAULT_GENERATION_CONFIG, isPlainEndlessPuzzle, } from '../config/generation-config';
import { trackConfigFor, trackPositionFor } from './track-map';
export class TrackLevelGenerator {
    constructor(track, config = DEFAULT_GENERATION_CONFIG) {
        this.track = track;
        this.config = config;
        /** Layout signatures this track produced, oldest first. Keyed on ORDINAL. */
        this.recent = [];
        this.lastOrdinal = 0;
        this.recipeChain = new RecipeChain(config);
    }
    get window() {
        return Math.max(0, this.config.antiRepetition.noRepeatWindow);
    }
    /**
     * The board for `ordinal` (1-based) of this track.
     *
     * Walking forward extends the window. Any other jump — a restart, a QA jump,
     * the Studio — resets it, because a window built from a different path would
     * make the board depend on where the player had been rather than on where
     * they are, and that is exactly the non-determinism `RecipeChain` documents.
     */
    generate(ordinal, options = {}) {
        const k = Math.max(1, Math.floor(ordinal));
        if (k !== this.lastOrdinal + 1)
            this.reset();
        const position = trackPositionFor(this.config, this.track, k);
        // Module 73 (WP-73.4) — on a track with `endlessPowered`, the ENDLESS
        // phase's powered gates follow the puzzle number (2 of every 3), not the
        // effective level. Ramp puzzles and other tracks pass nothing, so their
        // boards are byte-identical to before.
        const trackCfg = trackConfigFor(this.config, this.track);
        // `k > rampLength`, NOT `position.holding`: the track map already calls the
        // ramp's LAST puzzle holding (`k >= ramp`), and that puzzle is still ramp.
        const poweredCount = trackCfg.endlessPowered && k > trackCfg.rampLength
            ? isPlainEndlessPuzzle(trackCfg, k)
                ? 0
                : 1
            : undefined;
        const level = generateFreeLevel(position.effectiveLevel, {
            config: this.config,
            seed: position.variant,
            ...(poweredCount === undefined ? {} : { poweredCount }),
            recipeChain: this.recipeChain,
            avoidLayouts: [...this.recent],
            // Scoring is OFF on the runtime path (it runs the optimal-move BFS that
            // generation exists to avoid) and ON for the measurement harness. The
            // board itself does not depend on it — asserted in the WP-65.A2 scan.
            score: options.score === true,
            ...(options.solution === undefined ? {} : { solution: options.solution }),
        });
        this.recent.push(level.metrics.layoutSignature);
        while (this.recent.length > this.window)
            this.recent.shift();
        this.lastOrdinal = k;
        return { ...level, position: { ...position, ordinal: k, track: this.track } };
    }
    reset() {
        this.recent.length = 0;
        this.lastOrdinal = 0;
        this.recipeChain.reset();
    }
}
/**
 * One-shot generation for a track position, with no history.
 *
 * For callers that genuinely have no sequence — a preview, a fixture, a test.
 * A player's boards must come from `TrackLevelGenerator`, or they lose the
 * anti-repetition window entirely.
 */
export function generateForTrack(config, track, ordinal) {
    const position = trackPositionFor(config, track, ordinal);
    const level = generateFreeLevel(position.effectiveLevel, {
        config,
        seed: position.variant,
    });
    return { ...level, position: { ...position, ordinal: Math.max(1, Math.floor(ordinal)), track } };
}
